import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Public,
  CloudQueue,
  Speed,
  Analytics,
  Security,
  Assessment,
  CheckCircle,
  ArrowForward,
  Person,
  Business,
  DataUsage,
  Nature,
  GridView
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const Utilities = () => {
  const caseStudies = [
    {
      title: 'Smart Grid Implementation',
      description: 'A major utility company deployed smart grid technology with IoT sensors and AI analytics, improving grid efficiency by 35% and reducing outage times by 50%.',
      link: '#'
    },
    {
      title: 'Renewable Energy Integration',
      description: 'A utility provider integrated renewable energy sources with advanced grid management systems, increasing clean energy capacity by 40% while maintaining grid stability.',
      link: '#'
    },
    {
      title: 'Customer Service Digitalization',
      description: 'A utility company modernized their customer service with digital platforms and mobile apps, improving customer satisfaction by 45% and reducing service calls by 30%.',
      link: '#'
    }
  ];

  const keyStats = [
    { value: '35%', description: 'improvement in grid efficiency through smart grid technology.' },
    { value: '50%', description: 'reduction in outage times through predictive maintenance.' },
    { value: '40%', description: 'increase in clean energy capacity through renewable integration.' }
  ];

  const services = [
    {
      icon: <GridView sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Smart Grid',
      description: 'Advanced grid management systems with IoT sensors and real-time monitoring.'
    },
    {
      icon: <Nature sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Energy Management',
      description: 'Comprehensive energy management solutions for efficiency and sustainability.'
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Customer Analytics',
      description: 'Data-driven insights for improving customer service and operational efficiency.'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Grid Security',
      description: 'Advanced cybersecurity solutions for protecting critical infrastructure.'
    }
  ];

  const expert = {
    name: 'Maria Gonzalez',
    title: 'Vice President, Utilities',
    company: 'Scherstellen Technologie',
    image: '/api/placeholder/150/150',
    qa: [
      {
        question: 'How are utilities companies modernizing infrastructure for soaring power demand?',
        answer: 'Utilities are implementing smart grid technologies, renewable energy integration, and advanced analytics to modernize infrastructure and meet increasing power demand while improving efficiency and reliability.'
      },
      {
        question: 'What role does technology play in grid modernization?',
        answer: 'Technology enables real-time monitoring, predictive maintenance, automated responses, and integration of renewable energy sources that make the grid more efficient, reliable, and sustainable.'
      },
      {
        question: 'How are utilities addressing sustainability challenges?',
        answer: 'Utilities are investing in renewable energy, smart grid technologies, energy storage systems, and customer engagement platforms that promote energy efficiency and reduce environmental impact.'
      }
    ]
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 3,
                lineHeight: 1.2
              }}
            >
              Utilities
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: '#1976d2',
                mb: 2,
                fontWeight: 'bold'
              }}
            >
              Helping utilities companies modernize infrastructure
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                mb: 4,
                lineHeight: 1.6,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Reimagine grid through soaring power demand with smart technology solutions
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Overview Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  mb: 3
                }}
              >
                Overview
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Scherstellen Technologie helps utilities companies modernize infrastructure and reimagine grid through soaring power demand. We enable smart grid technologies, renewable energy integration, and digital transformation that drives efficiency and sustainability.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Utilities Technology Solutions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Case Studies Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Success Stories
          </Typography>
          <Grid container spacing={4}>
            {caseStudies.map((study, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {study.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {study.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      endIcon={<ArrowForward />}
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          color: '#1565c0',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      Learn more
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Statistics */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              mb: 6,
              textAlign: 'center'
            }}
          >
            3 things to know
          </Typography>
          <Grid container spacing={4}>
            {keyStats.map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {stat.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our Utilities Solutions
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {service.icon}
                      <Typography
                        variant="h5"
                        sx={{
                          ml: 2,
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Expert Q&A Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Industry Experts
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our experts, your challenges
          </Typography>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={expert.image}
                  sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    mb: 3
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 1
                  }}
                >
                  {expert.name}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1976d2',
                    mb: 1
                  }}
                >
                  {expert.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666'
                  }}
                >
                  {expert.company}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 4
                  }}
                >
                  Q&A with Scherstellen Technologie utilities expert, {expert.name}
                </Typography>
                
                {expert.qa.map((qa, index) => (
                  <Box key={index} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      Q: {qa.question}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6,
                        mb: 3
                      }}
                    >
                      A: {qa.answer}
                    </Typography>
                    {index < expert.qa.length - 1 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Connect with an expert to discuss your IT Transformation
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Let's help you modernize and transform your utilities operations
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Contact Us
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Utilities;
